import * as React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import type { PopoverMenuTouchableOptionProps as Option } from 'modules/core/components/PopoverMenu'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import { employeeUser } from 'modules/core/store/collections/users'
import app from 'modules/core/store/models/app'

import { BecomeModal } from 'modules/authentication/public'
import BecomeHint from 'modules/employees/components/Actions/BecomeHint'
import useShowBecomeHint from 'modules/employees/hooks/useShowBecomeHint'
import { useUnterminateEmployee } from 'modules/employees/hooks/useUnterminateEmployee'
import type Employee from 'modules/employees/store/models/Employee'
import permissionGroups from 'modules/permissions/store/collections/permissionGroups'
import {
  default as policyAuthorizations,
  default as policyAuthorizationsCollection,
} from 'modules/permissions/store/collections/policyAuthorizations'

import { CreatePermissionContext } from '../../../CreatePermissionContext'

interface Props {
  employee: Employee
  status: string
  isFirst: boolean
}

const ActionButtons = ({ status, employee, isFirst }: Props) => {
  const { pathname } = useLocation()
  const [become, setBecome] = React.useState(false)
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const canCreateEmployee = React.useContext(CreatePermissionContext).authorization
  const employeeId = employee.attributes.id
  const { shouldShowHint, onCloseHint } = useShowBecomeHint()
  const { unterminate } = useUnterminateEmployee()

  async function sendConfirmationEmail(event: Event) {
    const api = ApiClient()
    const user = employeeUser(employee)
    event.stopPropagation()

    try {
      await api.post('/invitations', {
        access_id: user.attributes.id,
      })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(JSON.stringify(error))
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_saving'),
      })

      return
    }
    app.setFlash({
      type: 'correct',
      message: i18n.t('employees.invitation_sent'),
    })

    user.set({
      invited: true,
    })
  }

  async function unterminateEmployee() {
    const { data, error } = await unterminate({ id: employee.id })

    if (data?.employee?.id) {
      employee.set({ terminated_on: null })
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
        time: 2000,
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: error || i18n.t('common.save_error'),
        time: 2000,
      })
    }
  }

  const user = employeeUser(employee)
  const canBecome = policyAuthorizationsCollection.for('employee.become', user)
  const employeeIsInAdminGroup = !!permissionGroups.adminGroup?.users.includes(user)
  const options: Option[] = []

  const handleBecome = () => {
    track(trackingEvents.employee.become.startBecome, { from: pathname })
    onCloseHint()
    setBecome(true)
  }

  if (user.isCurrent()) return null

  const becomeAction = {
    title: i18n.t('employees.become.action', { name: user.firstName }),
    description: i18n.t('employees.become.action_description'),
    onClick: handleBecome,
  }

  const terminateAction = {
    title: i18n.t('employees.terminate'),
    onClick: () => navigate(navigation.employees.id.terminate.fullPath({ id: employee.id })),
    description: i18n.t('employees.termination.action_description'),
    danger: true,
  }

  switch (status) {
    case 'accepted':
      if (canBecome) {
        options.push(becomeAction)
      }

      options.push(terminateAction)

      break

    case 'active':
      if (employeeIsInAdminGroup) {
        options.push(terminateAction)
      } else {
        if (canBecome) {
          options.push(becomeAction)
        }

        options.push(terminateAction)
      }

      break
    case 'invited':
      options.push(
        {
          title: i18n.t('employees.reinvite'),
          onClick: sendConfirmationEmail,
        },
        terminateAction
      )
      break
    case 'notInvited':
      options.push(
        {
          title: i18n.t('employee_form.invite_employee'),
          onClick: sendConfirmationEmail,
        },
        terminateAction
      )
      break
    case 'terminated':
      options.push({
        title: i18n.t('employees.untermination.action'),
        onClick: () => unterminateEmployee(),
        description: i18n.t('employees.untermination.action_description'),
      })
      break
  }

  const onActionsButtonClick = (opening: boolean) => {
    if (opening) {
      // We fetch some of the policies that we need to render the options here instead
      // of doing so at the list level, this is done to avoid hammering the backend with
      // checks for potentially thousands of employees in big companies.
      // This solution is not ideal as it will cause a layout shift in the popover if any of
      // them end up being authorized, but it is a tradeoff we're willing to make for now.
      if (hasFeature('become_enabled')) {
        policyAuthorizations.authorizePolicies('employee.become', [employeeUser(employee)])
      }
    }
  }

  return (
    <Box flexDirection="row" gap="s8" justifyContent="flexEnd" paddingRight="s24">
      {canCreateEmployee && (
        <>
          <BecomeHint open={shouldShowHint && isFirst} onClose={onCloseHint}>
            <PopoverMenu type="list" onClick={onActionsButtonClick}>
              <PopoverMenu.Trigger>
                <Button type="secondary" onClick={onCloseHint} iconName="ellipsis" />
              </PopoverMenu.Trigger>
              <PopoverMenu.Content side="bottom" align="center">
                {options.map(({ title, description, onClick, danger }, index) => (
                  <PopoverMenu.TouchableOption
                    key={index}
                    danger={danger}
                    title={title}
                    onClick={onClick}
                    description={description}
                  />
                ))}
              </PopoverMenu.Content>
            </PopoverMenu>
          </BecomeHint>
          {become && (
            <BecomeModal
              accessId={user.id}
              firstName={user.firstName}
              onClose={() => {
                setBecome(false)
              }}
            />
          )}
        </>
      )}
      <Button
        type="secondary"
        onClick={() => navigate(navigation.employees.id.fullPath({ id: employeeId }))}
        iconName="arrow-right"
        extra={{ 'data-intercom-target': 'employee-list-view-button' }}
      />
    </Box>
  )
}

export default observer(ActionButtons)
